import { useState } from 'react'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { screenSizes } from '@shipt/design-system-themes'
import { ChevronDownIcon, ChevronUpIcon } from '@shipt/design-system-icons'
import { type UniversalProductLocation } from '@/features/shop/services/UniversalProduct/types'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { TextButton } from '@shipt/design-system-buttons'
import { HDivider } from '@/features/shared/components/Dividers'

type Props = {
  locations: UniversalProductLocation[]
  zip: string
  onUpdateZipClick: () => void
  selectedLocation: UniversalProductLocation | undefined
  updateSelectedLocation: (loc: UniversalProductLocation) => void
}

export const AvailableStores = ({
  locations = [],
  zip = '',
  onUpdateZipClick,
  selectedLocation,
  updateSelectedLocation,
}: Props) => {
  const [availableStoresVisible, setAvailableStoresVisible] = useState(true)

  const handleAvailableStoresToggle = () => {
    setAvailableStoresVisible(!availableStoresVisible)
  }

  const handleAvailableStoreSelect = (store: UniversalProductLocation) => {
    setAvailableStoresVisible(false)
    updateSelectedLocation(store)
  }
  const availableStores = locations.filter((l) => l.store_id !== selectedLocation?.store_id)
  const ArrowIcon = availableStoresVisible ? ChevronDownIcon : ChevronUpIcon

  return (
    <StoreSelector>
      {!!selectedLocation && (
        <Row wrap>
          <SelectedStoreLogo>
            <Image
              src={selectedLocation.store_circular_image_url}
              alt={selectedLocation.store_name}
              width={48}
              height={48}
            />
          </SelectedStoreLogo>
          <div>
            <Body strong size="xl">
              {selectedLocation.store_name}
            </Body>
            <Row spacing="xs" align="center">
              <Body>Available in</Body>
              <Body strong size="md">
                {zip}
              </Body>
            </Row>
            <UpdateZipButton onClick={onUpdateZipClick}>Update ZIP code</UpdateZipButton>
          </div>
        </Row>
      )}
      {availableStores.length > 0 && (
        <>
          <Divider />
          <AvailableStoresToggle onClick={handleAvailableStoresToggle}>
            <Body strong size="md" align="left">
              Select another store
            </Body>
            <ChevronWrapper>
              <ArrowIcon size="md" />
            </ChevronWrapper>
          </AvailableStoresToggle>
          {!!availableStoresVisible && (
            <AvailableStoresRow spacing="md">
              {availableStores.map((store) => {
                return (
                  <TransparentButton
                    key={store.store_id}
                    onClick={() => handleAvailableStoreSelect(store)}
                  >
                    <Image
                      src={store.store_circular_image_url}
                      alt={store.store_name}
                      width={48}
                      height={48}
                    />
                  </TransparentButton>
                )
              })}
            </AvailableStoresRow>
          )}
        </>
      )}
    </StoreSelector>
  )
}

const StoreSelector = styled.div`
  background-color: ${({ theme }) => theme.gray50};
  box-shadow: 0 0 2px rgb(0 0 0 / 20%);
  border-radius: 8px;
  padding: 1rem;
`

const SelectedStoreLogo = styled.div`
  margin-right: 16px;
`

const UpdateZipButton = styled(TextButton)`
  margin-top: 4px;
`

const Divider = styled(HDivider)`
  margin-top: 24px;
`

const AvailableStoresRow = styled(Row)`
  flex-wrap: wrap;
`

const ChevronWrapper = styled.div`
  color: ${({ theme }) => theme.gray900};
  display: none;
  padding-right: 0.5rem;

  @media ${screenSizes.tablet} {
    display: inline;
  }
`

const AvailableStoresToggle = styled(TransparentButton)`
  width: 100%;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`
